import { useSignal } from '@preact/signals';
import { useEffect, useRef } from 'preact/hooks';

import type { AgendaEvent, AgendaReminder, MemberIds } from '@amumin/api-client';
import { t } from '@amumin/i18n';
import { Button, Checkbox, Notice, TextField } from '@amumin/ui';
import { Avatar, cx, Icon, memberStyle, Pictogram } from '@amumin/ui-core';

import { api } from '../api.js';
import { membersOf, type MemberLite } from '../lib/agenda.js';
import {
  REMINDER_DEFAULT_ICON,
  REMINDER_MAX,
  REMINDER_OFFSETS,
  REMINDER_TITLE_MAX,
  draftKey,
  effectiveReminders,
  everyoneMeansKids,
  fireTime,
  formatOffset,
  reminderChipText,
  reminderPeople,
  suggestReminders,
  type ReminderDraft,
} from '../lib/reminders.js';
import { mutate } from '../lib/ui.js';
import { appText } from '../state/session.js';
import { ChecklistEditor, ChecklistPreview, ChecklistTicksView } from './ChecklistEditor.js';
import { MemberAvatars, MemberPicker } from './MemberPicker.js';
import { PictoField } from './PictoField.js';
import styles from './ReminderEditor.module.css';

// Herinneringen in de afspraak-editor (0021; founder: "voetbaltraining: een half uur van tevoren
// roept het scherm 'Yakub, Klaarmaken voor trainen' met een groot pictogram voor omkleden").
// Eigen component zodat de editor zelf nauwelijks verandert: een lijst van herinneringen, "+
// Herinnering" opent een klein formulier (wanneer, tekst met voorstellen, pictogram, voor wie,
// voorlezen, geluid) met een voorbeeld zoals de tablet het toont.
// Review r1: een open, ingevuld formulier gaat mee bij "Afspraak opslaan" (flushRef); leden die uit
// de afspraak gaan, gaan ook uit de herinneringen (met een korte melding); "Voor wie" zonder keuze
// roept de kinderen van de afspraak.

/** Resultaat van `flushRef`: `ok: false` = het open formulier klopt niet (opslaan tegenhouden). */
export interface ReminderFlush {
  ok: boolean;
  list: ReminderDraft[];
}

export interface ReminderEditorProps {
  value: ReadonlyArray<ReminderDraft>;
  onChange: (next: ReminderDraft[]) => void;
  /** De afspraak zoals hij nu in het formulier staat. */
  event: {
    title: string;
    icon: string | null;
    category: string | null;
    start: string;
    allDay: boolean;
    memberIds: MemberIds;
    /**
     * Leden bij het openen van een bestaande keer (review r2): alleen een wijziging t.o.v. deze
     * leden snoeit herinneringen. Ontbreekt/null = nieuwe afspraak (snoeien tegen memberIds).
     */
    baseMemberIds?: MemberIds | null;
    /** Bestaande herhaling: bij bijsnoeien uitleggen dat "Alleen deze" de reeks heel laat. */
    recurring?: boolean;
    /**
     * Keuzes bij "Voor wie" (release-b r1): bij een bestaande herhaling waarvan de ouder de leden
     * niet wijzigde, de leden van de reeks (een herinnering hoort bij de reeks; de server toetst
     * daartegen). Zo staat Omer, die "alleen deze keer" meedoet, er niet als keuze tussen, en
     * Yakub, die deze ene keer ziek is, wel. Ontbreekt/null = de leden in het formulier.
     */
    choiceMemberIds?: MemberIds | null;
  };
  members: ReadonlyArray<MemberLite>;
  /**
   * De editor roept dit aan vóór opslaan: een open formulier wordt dan toegevoegd (of houdt het
   * opslaan tegen met een foutmelding), bij een hele dag vervallen de herinneringen.
   */
  flushRef?: { current: (() => ReminderFlush) | null };
}

interface FormState {
  draft: ReminderDraft;
  custom: boolean;
  iconTouched: boolean;
  errors: { title?: string | undefined; offset?: string | undefined; who?: string | undefined };
}

export function ReminderEditor({ value, onChange, event, members, flushRef }: ReminderEditorProps) {
  const a = appText.value;
  const r = a.reminders;
  const form = useSignal<FormState | null>(null);
  const choiceIds = event.choiceMemberIds ?? event.memberIds;
  const eventMembers = choiceIds === 'everyone' ? [...members] : membersOf(choiceIds, members);
  const suggestions = suggestReminders(event, r);
  const nameOf = (id: string): string => members.find((x) => x.id === id)?.display_name ?? '';
  const joinNames = (ids: ReadonlyArray<string>): string => {
    const names = ids.map(nameOf).filter((n) => n.length > 0);
    if (names.length <= 1) return names[0] ?? '';
    return `${names.slice(0, -1).join(', ')} ${r.and} ${names[names.length - 1] ?? ''}`;
  };

  // Leden uit de afspraak gehaald (review r2): `value` blijft de lijst zoals de ouder hem maakte;
  // wat er bij opslaan voor de reeks overblijft, is afgeleid (effectiveReminders). Dat gebeurt
  // alleen als de ouder hier de leden wijzigt, nooit bij het openen van een keer waar een kind
  // "alleen deze keer" niet meedoet. Weer aanvinken = de herinnering is terug. Nooit stil van
  // "Yakub" naar "iedereen".
  const base = event.baseMemberIds === undefined ? null : event.baseMemberIds;
  const shown = effectiveReminders(value, event.memberIds, base);
  const list = shown.drafts;
  const notes = shown.notes.map((n) => {
    const many = n.gone.length > 1;
    const tpl = n.removed
      ? many
        ? r.prunedRemovedMany
        : r.prunedRemoved
      : many
        ? r.prunedChangedMany
        : r.prunedChanged;
    return t(tpl, { names: joinNames(n.gone), title: n.title });
  });
  const memberKey = event.memberIds === 'everyone' ? '*' : event.memberIds.join(',');
  const mounted = useRef(false);
  useEffect(() => {
    // Het open formulier: een kind dat de ouder net uit de afspraak haalde, gaat er ook uit.
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    const f = form.value;
    if (!f || f.draft.member_ids.length === 0) return;
    const pruned = effectiveReminders([f.draft], event.memberIds, base);
    const note = pruned.notes[0];
    if (!note) return;
    form.value = {
      ...f,
      draft: { ...f.draft, member_ids: pruned.drafts[0]?.member_ids ?? [] },
      errors: note.removed ? { ...f.errors, who: r.whoRequired } : f.errors,
    };
  }, [memberKey]);

  const startNew = () => {
    const first = suggestions[0];
    form.value = {
      draft: {
        key: draftKey(),
        offset_minutes: 30,
        title: first ? first.title : '',
        icon: first ? first.icon : REMINDER_DEFAULT_ICON,
        member_ids: [],
        speak: true,
        sound: true,
        checklist: [],
      },
      custom: false,
      iconTouched: false,
      errors: {},
    };
  };

  const startEdit = (d: ReminderDraft) => {
    form.value = {
      draft: { ...d, member_ids: [...d.member_ids], checklist: [...(d.checklist ?? [])] },
      custom: !(REMINDER_OFFSETS as ReadonlyArray<number>).includes(d.offset_minutes),
      iconTouched: true,
      errors: {},
    };
  };

  const patch = (changes: Partial<ReminderDraft>, extra: Partial<FormState> = {}) => {
    const f = form.value;
    if (!f) return;
    form.value = { ...f, ...extra, draft: { ...f.draft, ...changes } };
  };

  /** Controleert het open formulier; geeft de nieuwe lijst, of null (fouten staan dan in beeld). */
  const committed = (): ReminderDraft[] | null => {
    const f = form.value;
    if (!f) return [...value];
    const title = f.draft.title.trim();
    const errors: FormState['errors'] = {};
    if (!title) errors.title = r.textRequired;
    else if (title.length > REMINDER_TITLE_MAX) errors.title = r.textTooLong;
    const off = f.draft.offset_minutes;
    if (!Number.isInteger(off) || off < 1 || off > 1440) errors.offset = r.offsetInvalid;
    if (f.errors.who && f.draft.member_ids.length === 0) errors.who = f.errors.who;
    if (errors.title || errors.offset || errors.who) {
      form.value = { ...f, errors };
      focusError(errors);
      return null;
    }
    const next = { ...f.draft, title };
    const exists = value.some((x) => x.key === next.key);
    return exists ? value.map((x) => (x.key === next.key ? next : x)) : [...value, next];
  };

  const commit = () => {
    const list = committed();
    if (!list) return;
    onChange(list);
    form.value = null;
  };

  if (flushRef) {
    flushRef.current = (): ReminderFlush => {
      if (event.allDay) {
        form.value = null;
        return { ok: true, list: [] };
      }
      const list = committed();
      if (!list) return { ok: false, list: [...value] };
      if (form.value) {
        onChange(list);
        form.value = null;
      }
      return { ok: true, list };
    };
  }

  const f = form.value;
  const kidsDefault = everyoneMeansKids(eventMembers);
  return (
    <section class={cx('field-group', styles.section)} data-name="reminder-editor">
      <div class={styles.head}>
        <span class="field-label">
          <Icon id="alarm-clock" size={18} /> {r.section}
        </span>
        {!f && value.length < REMINDER_MAX && !event.allDay ? (
          <Button name="reminder-add" size="small" icon="plus" onPress={startNew}>
            {r.add}
          </Button>
        ) : null}
      </div>
      <p class="field-hint">
        {event.allDay ? (list.length > 0 ? r.allDayDrop : r.allDayHint) : r.sectionHint}
      </p>
      {notes.length > 0 ? (
        <Notice kind="info" name="reminder-pruned">
          {notes.map((n) => (
            <span key={n} class={styles.note}>
              {n}
            </span>
          ))}
          {event.recurring ? <span class={styles.note}>{r.prunedThisHint}</span> : null}
        </Notice>
      ) : null}

      {list.length > 0 ? (
        <ul
          class={cx(styles.list, event.allDay && styles.listDead)}
          data-name="reminder-list"
          data-dead={event.allDay ? 'true' : undefined}
        >
          {list.map((d) => (
            <li key={d.key} class={styles.item} data-name="reminder-item">
              <span class={styles.itemPicto}>
                <Pictogram icon={d.icon} label={d.title} size={32} decorative />
              </span>
              <span class={styles.itemText}>
                <span class={styles.itemTitle}>{d.title}</span>
                <span class={styles.itemWhen}>
                  {t(r.before, { when: formatOffset(d.offset_minutes, r) })}
                  {!event.allDay ? ` · ${fireTime(event.start, d.offset_minutes)}` : ''}
                  {(d.checklist ?? []).length > 0
                    ? ` · ${a.checklist.title} (${(d.checklist ?? []).length})`
                    : ''}
                </span>
              </span>
              <MemberAvatars
                memberIds={reminderPeople(d.member_ids, eventMembers).map((x) => x.id)}
                members={members}
                max={3}
              />
              <span class={styles.itemActions}>
                {!event.allDay ? (
                  <Button
                    name="reminder-edit"
                    size="small"
                    variant="ghost"
                    icon="pencil"
                    label={r.edit}
                    onPress={() => startEdit(d)}
                  />
                ) : null}
                <Button
                  name="reminder-remove"
                  size="small"
                  variant="ghost"
                  icon="trash-2"
                  label={r.remove}
                  onPress={() => onChange(value.filter((x) => x.key !== d.key))}
                />
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {value.length >= REMINDER_MAX && !f ? <p class="field-hint">{r.max}</p> : null}

      {f && !event.allDay ? (
        <div
          class={styles.form}
          data-name="reminder-form"
          role="group"
          aria-label={value.some((x) => x.key === f.draft.key) ? r.editTitle : r.addTitle}
        >
          <div class={styles.formHead}>
            <span class={styles.formTitle}>
              {value.some((x) => x.key === f.draft.key) ? r.editTitle : r.addTitle}
            </span>
            <Button
              name="reminder-cancel"
              size="small"
              variant="ghost"
              icon="x"
              label={r.closeForm}
              onPress={() => (form.value = null)}
            />
          </div>
          <fieldset class={styles.fieldset}>
            <legend class="field-label">{r.when}</legend>
            <div class={styles.chips}>
              {REMINDER_OFFSETS.map((n) => {
                const on = !f.custom && f.draft.offset_minutes === n;
                return (
                  <label key={n} class={cx(styles.chip, on && styles.chipOn)}>
                    <input
                      type="radio"
                      name="reminder-when"
                      checked={on}
                      onChange={() =>
                        patch(
                          { offset_minutes: n },
                          { custom: false, errors: { ...f.errors, offset: undefined } },
                        )
                      }
                    />
                    <span>{t(r.before, { when: formatOffset(n, r) })}</span>
                  </label>
                );
              })}
              <label class={cx(styles.chip, f.custom && styles.chipOn)}>
                <input
                  type="radio"
                  name="reminder-when"
                  checked={f.custom}
                  onChange={() => patch({}, { custom: true })}
                />
                <span>{r.customWhen}</span>
              </label>
            </div>
            {f.custom ? (
              <div class={styles.custom}>
                <TextField
                  id="reminder-offset"
                  label={r.customLabel}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={1440}
                  value={String(f.draft.offset_minutes)}
                  onInput={(v) => patch({ offset_minutes: Math.round(Number(v)) || 0 })}
                  hint={r.customHint}
                  error={f.errors.offset}
                />
              </div>
            ) : null}
          </fieldset>

          <div>
            <TextField
              id="reminder-title"
              label={r.text}
              value={f.draft.title}
              onInput={(v) => patch({ title: v }, { errors: { ...f.errors, title: undefined } })}
              placeholder={r.textPlaceholder}
              maxLength={REMINDER_TITLE_MAX}
              error={f.errors.title}
            />
            {suggestions.length > 0 ? (
              <div class={styles.suggest} role="group" aria-label={r.suggestions}>
                {suggestions.map((s) => (
                  <button
                    key={s.title}
                    type="button"
                    class={cx(styles.suggestChip, f.draft.title === s.title && styles.chipOn)}
                    data-name="reminder-suggestion"
                    onClick={() =>
                      patch(
                        { title: s.title, ...(f.iconTouched ? {} : { icon: s.icon }) },
                        { errors: { ...f.errors, title: undefined } },
                      )
                    }
                  >
                    <Pictogram icon={s.icon} label={s.title} size={20} decorative />
                    <span>{s.title}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {/* 0031: de checklist hoort bij de tekst (founder: "bij 'tekst' onder nieuwe herinnering"). */}
          <ChecklistEditor
            value={f.draft.checklist ?? []}
            onChange={(next) => patch({ checklist: next })}
            event={event}
            reminderTitle={f.draft.title}
          />

          <PictoField
            id="reminder-icon"
            label={r.picto}
            value={f.draft.icon}
            onChange={(icon) =>
              patch({ icon: icon ?? REMINDER_DEFAULT_ICON }, { iconTouched: true })
            }
            fallbackLabel={f.draft.title}
          />

          <MemberPicker
            id="reminder-members"
            label={r.forWho}
            members={eventMembers}
            value={
              f.draft.member_ids.length === 0 && !f.errors.who ? 'everyone' : f.draft.member_ids
            }
            onChange={(v) =>
              patch(
                { member_ids: v === 'everyone' ? [] : v },
                { errors: { ...f.errors, who: undefined } },
              )
            }
            allowEveryone
            everyoneLabel={kidsDefault ? r.everyoneKids : undefined}
            error={f.errors.who}
          />

          <div class={styles.toggles}>
            <Checkbox
              id="reminder-speak"
              label={r.speak}
              hint={r.speakHint}
              checked={f.draft.speak}
              onChange={(v) => patch({ speak: v })}
            />
            <Checkbox
              id="reminder-sound"
              label={r.sound}
              hint={r.soundHint}
              checked={f.draft.sound}
              onChange={(v) => patch({ sound: v })}
            />
          </div>

          <ReminderPreview draft={f.draft} event={event} members={eventMembers} />

          <div class={styles.formActions}>
            {/* Eén primaire knop per scherm (DESIGN §2): "Afspraak opslaan" blijft de primaire;
                een open formulier gaat daar ook gewoon mee. */}
            <Button name="reminder-done" variant="secondary" icon="check" onPress={commit}>
              {value.some((x) => x.key === f.draft.key) ? r.confirmSave : r.confirmAdd}
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function focusError(errors: FormState['errors']): void {
  const id = errors.offset
    ? 'reminder-offset'
    : errors.title
      ? 'reminder-title'
      : errors.who
        ? 'reminder-members'
        : null;
  if (!id) return;
  setTimeout(() => {
    const el = document.getElementById(id);
    if (!el) return;
    const target = el.tagName === 'FIELDSET' ? el.querySelector('input') : el;
    if (target) {
      target.scrollIntoView({ block: 'center' });
      target.focus();
    }
  }, 0);
}

/** Voorbeeld zoals de tablet roept: gezicht + naam, groot pictogram, titel, afspraak, "Oké!". */
function ReminderPreview({
  draft,
  event,
  members,
}: {
  draft: ReminderDraft;
  event: ReminderEditorProps['event'];
  members: ReadonlyArray<MemberLite>;
}) {
  const r = appText.value.reminders;
  const a = appText.value;
  // Precies wie de tablet roept (kinderen eerst); iedereen die genoemd wordt, staat in beeld.
  const shown = reminderPeople(draft.member_ids, members).slice(0, 6);
  const first = shown.length === 1 ? shown[0] : null;
  return (
    <figure
      class={styles.preview}
      data-name="reminder-preview"
      style={first ? memberStyle(first.color_key) : undefined}
    >
      <figcaption class={styles.previewLabel}>{r.preview}</figcaption>
      <div class={styles.previewCard}>
        <span class={styles.previewPicto}>
          <Pictogram icon={draft.icon} label={draft.title} size={96} decorative />
        </span>
        <p class={styles.previewTitle}>{draft.title || r.textPlaceholder}</p>
        <p class={styles.previewContext}>
          {event.icon ? <Pictogram icon={event.icon} label="" size={20} decorative /> : null}
          <span>
            {t(r.previewContext, {
              title: event.title.trim() || r.previewNoTitle,
              time: event.allDay ? '' : event.start,
            })}
          </span>
        </p>
        <ChecklistPreview list={draft.checklist ?? []} />
        <div class={cx(styles.previewKids, shown.length > 3 && styles.previewKidsMany)}>
          {shown.length > 0 ? (
            shown.map((kid) => (
              <span
                key={kid.id}
                class={styles.previewKid}
                style={memberStyle(kid.color_key)}
                data-name="reminder-preview-kid"
              >
                <Avatar
                  name={kid.display_name}
                  colorKey={kid.color_key}
                  icon={kid.avatar_icon}
                  size="medium"
                  decorative
                />
                <span class={styles.previewName}>{kid.display_name}</span>
                <span class={styles.previewOk} aria-hidden="true">
                  {r.previewOk}
                </span>
              </span>
            ))
          ) : (
            <span class={styles.previewKid}>
              <span class={styles.previewName}>{a.common.everyone}</span>
              <span class={styles.previewOk} aria-hidden="true">
                {r.previewOk}
              </span>
            </span>
          )}
        </div>
      </div>
    </figure>
  );
}

/**
 * Chips op het afspraakblad: "⏰ 30 min vooraf · Klaarmaken voor trainen". Bij een herhaling per
 * chip "Alleen deze keer niet" (review r2; PATCH /v1/app/occurrences/:id { skip_reminder_ids }),
 * en een overgeslagen herinnering staat er gedimd bij met "Toch roepen".
 */
export function ReminderChips({
  reminders,
  members,
  occurrence,
  onDone,
}: {
  reminders: ReadonlyArray<AgendaReminder> | undefined;
  members: ReadonlyArray<MemberLite>;
  /** De keer op het blad; met een herhaling verschijnt "Alleen deze keer niet". */
  occurrence?: AgendaEvent | undefined;
  /** Na een geslaagde wijziging (het blad sluit, de agenda ververst). */
  onDone?: (() => void) | undefined;
}) {
  const a = appText.value;
  const r = a.reminders;
  const busy = useSignal(false);
  const skipped = occurrence?.skipped_reminders ?? [];
  if ((!reminders || reminders.length === 0) && skipped.length === 0) return null;
  const canSkip = !!occurrence && occurrence.recurring && !occurrence.cancelled;
  const skippedIds = skipped.map((x) => x.id);

  const setSkip = async (next: string[], message: string, undoTo: string[]) => {
    if (!occurrence || busy.value) return;
    busy.value = true;
    const oid = occurrence.occurrence_id;
    const res = await mutate(
      () =>
        api.app.occurrences.update(oid, {
          skip_reminder_ids: next,
          if_version: occurrence.override_version ?? 0,
        }),
      {
        success: message,
        undo: async () => {
          if (res) {
            await api.app.occurrences.update(oid, {
              skip_reminder_ids: undoTo,
              if_version: res.override_version,
            });
          }
        },
      },
    );
    busy.value = false;
    if (res && onDone) onDone();
  };

  const chip = (rem: AgendaReminder, off: boolean) => (
    <li
      key={rem.id}
      class={cx(styles.sheetChip, off && styles.sheetChipOff)}
      data-name={off ? 'reminder-chip-skipped' : 'reminder-chip'}
      aria-label={t(r.chipLabel, { when: formatOffset(rem.offset_minutes, r), title: rem.title })}
    >
      <span class={styles.sheetChipMain}>
        <span aria-hidden="true">⏰</span>
        <span class={styles.sheetChipText}>{reminderChipText(rem, r)}</span>
        {off ? (
          <span class={styles.sheetChipSkipped}>{r.skippedChip}</span>
        ) : (
          <span class={styles.sheetChipTime}>{rem.fire_hhmm}</span>
        )}
        <MemberAvatars memberIds={rem.member_ids} members={members} max={3} />
      </span>
      {canSkip ? (
        <span class={styles.sheetChipAction}>
          {off ? (
            <Button
              name="reminder-unskip"
              size="small"
              variant="ghost"
              icon="rotate-ccw"
              disabled={busy.value}
              label={t(r.unskipLabel, { title: rem.title })}
              onPress={() =>
                void setSkip(
                  skippedIds.filter((id) => id !== rem.id),
                  t(r.unskipDone, { title: rem.title }),
                  skippedIds,
                )
              }
            >
              {r.unskip}
            </Button>
          ) : (
            <Button
              name="reminder-skip"
              size="small"
              variant="ghost"
              icon="volume-x"
              disabled={busy.value}
              label={t(r.skipOnceLabel, { title: rem.title })}
              onPress={() =>
                void setSkip(
                  skippedIds.concat(rem.id),
                  t(r.skipOnceDone, { title: rem.title }),
                  skippedIds,
                )
              }
            >
              {r.skipOnce}
            </Button>
          )}
        </span>
      ) : null}
    </li>
  );

  return (
    <section class="sheet-section" data-name="event-reminders">
      <h3 class="section-label">{r.section}</h3>
      <ul class={styles.chipList}>
        {(reminders ?? []).map((rem) => chip(rem, false))}
        {skipped.map((rem) => chip(rem, true))}
      </ul>
      {/* 0031: wat er per kind al in de tas zit (vinkjes van de tablets, per keer). */}
      {(reminders ?? []).map((rem) =>
        rem.checklist && rem.checklist.length > 0 ? (
          <ChecklistTicksView key={`ticks-${rem.id}`} reminder={rem} members={members} />
        ) : null,
      )}
    </section>
  );
}
